/**
 * Full translation orchestration hook for the per-question answer
 * views (ResponsesPanel Sheet + inline ResponsesZone).
 *
 * Ported from the pre-redesign inline table so the panel
 * restores the complete experience researchers had:
 *   - Switch between already-translated languages
 *   - Trigger translation of a new language
 *   - Poll progress with stability-based completion detection
 *   - Delete existing translations
 *   - Propagate completion via query cache invalidation so other
 *     views (main Transcripts tab, transcript detail) pick up the
 *     new data without a full reload
 *
 * The stability detection (3 consecutive polls at the same count)
 * exists because the backend translates ALL project conversations,
 * not just those visible per question — we can't compare against an
 * expected total, so we conclude completion when the count stops
 * changing.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import { QueryTag, SearchParam } from '@common/ts';
import { getErrorMessage } from '@common/utils';
import {
  useGetTranslationLanguages,
  useGetTranslationProgress,
} from '@/reactQuery/get';
import { useMutateGenerateTranscriptsTranslation } from '@/reactQuery/post';
import { useMutateDeleteTranslation } from '@/reactQuery/delete';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import useCategoryStore from '@/store/CategoryStore';
import languagesList from '@/lang/languages/languagesList';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';

const STABLE_POLLS_THRESHOLD = 3; // ~15s of no progress means done
const TIMEOUT_MS = 15 * 60 * 1000; // 15 min hard cap

interface UseQuestionTranslationParams {
  /**
   * The source tag used in the TranslationsLoadingStore so we can tell
   * which view kicked off translation. Values are restricted to the
   * two shapes the store supports. Questions-pane views use
   * `'questions'`.
   */
  source?: 'questions' | 'transcripts';
  /** Question id — only needed when we want to invalidate per-question transcripts on completion. */
  questionId?: string;
  /** Conversation count for the translation request metadata. */
  conversationCount?: number;
}

/**
 * Hook return shape — a superset of what TranscriptsTranslationDropdown
 * needs, plus `languageParam` for API calls that respect the active
 * language.
 */
export interface UseQuestionTranslationResult {
  /** Non-"Original" language the user has selected, or null. */
  translationLanguage: string | null;
  /** Always resolved (`'Original'` when no override). */
  activeLanguage: string;
  /** The language currently being translated, or null when idle. */
  pendingLanguage: string | null;
  /** True while a translation is in flight for this project + source. */
  isTranslating: boolean;
  /** Languages the project has already been translated into (includes `'Original'`). */
  translatedLanguages: string[];
  /** Full supported languages minus the ones already translated. */
  unTranslatedLanguages: string[];
  /** Select an already-translated language, or start translating a new one. */
  handleLanguageSelection: (language: string) => Promise<void>;
  /** Delete an existing translation. */
  handleDeleteLanguage: (language: string) => Promise<void>;
  /**
   * The value to pass to API calls as the `language` query param —
   * null when the user wants the original, otherwise the language
   * string.
   */
  languageParam: string | null;
}

function useQuestionTranslation({
  source = 'questions',
  questionId,
  conversationCount,
}: UseQuestionTranslationParams = {}): UseQuestionTranslationResult {
  const { project_id: projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const categoryType = useCategoryStore((state) => state.categoryType);
  const filterId = searchParams.get(SearchParam.FILTER_ID);

  const { translatingProjects, setTranslatingProjects } =
    useTranslationsLoadingStore();

  // Which translation (if any) is in progress for this project + source?
  const currentProjectTranslating = useMemo(
    () =>
      translatingProjects?.find(
        (p) => p.projectId === projectId && p.source === source,
      ),
    [translatingProjects, projectId, source],
  );
  const isTranslating = !!currentProjectTranslating;
  const pendingLanguage = currentProjectTranslating?.language || null;

  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const activeLanguage = translationLanguage || 'Original';

  // ── Language lists ──────────────────────────────────────────────

  const { data: languages = [], refetch: refetchLanguages } =
    useGetTranslationLanguages(projectId);
  const translatedLanguages = useMemo(
    () => getSortedTranslatedLanguages(['Original'], languages),
    [languages],
  );
  const unTranslatedLanguages = useMemo(
    () =>
      languagesList.language
        .map(({ label }) => label)
        .filter((lang) => !languages.includes(lang) && lang !== 'Original'),
    [languages],
  );

  // ── Mutations + polling ────────────────────────────────────────

  const { mutateAsync: triggerTranslation } =
    useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: deleteTranslation } =
    useMutateDeleteTranslation(projectId);

  const { data: progressData, dataUpdatedAt: progressUpdatedAt } =
    useGetTranslationProgress(projectId, pendingLanguage);

  // Refs for stability detection + cleanup guards.
  const isCompletingRef = useRef(false);
  const lastCountRef = useRef<number>(0);
  const stableCountRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  // Reset stability counters when translation begins or ends.
  useEffect(() => {
    if (isTranslating && !startTimeRef.current) {
      startTimeRef.current = Date.now();
      lastCountRef.current = 0;
      stableCountRef.current = 0;
    }
    if (!isTranslating) {
      startTimeRef.current = 0;
    }
  }, [isTranslating]);

  // Clear an invalid language from the URL on mount — e.g. the
  // language was deleted in another tab. Without this the view would
  // try (and fail) to fetch transcripts in a non-existent language.
  useEffect(() => {
    if (
      translationLanguage &&
      !translatedLanguages.includes(translationLanguage) &&
      !isTranslating
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete(SearchParam.TRANSLATION_LANGUAGE);
      setSearchParams(next);
    }
    // Only re-run when translationLanguage / available languages change,
    // not on every searchParams bump (would cause a flash clear).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationLanguage, translatedLanguages.join(','), isTranslating]);

  // Stability-based completion detection. The backend progress count
  // grows as conversations get translated; when it stops changing for
  // STABLE_POLLS_THRESHOLD consecutive polls we treat it as complete.
  // A hard timeout covers the case where polling never converges.
  useEffect(() => {
    if (!pendingLanguage || !isTranslating || !progressData || isCompletingRef.current) {
      return;
    }

    const translatedCount = progressData.count;

    // Timeout guard
    const elapsed = Date.now() - startTimeRef.current;
    if (elapsed >= TIMEOUT_MS) {
      setTranslatingProjects((prev) =>
        prev?.filter(
          (p) => !(p.projectId === projectId && p.source === source),
        ) || [],
      );
      toast.error(
        `Translation to ${pendingLanguage} is taking longer than expected. Please try again or contact support if the issue persists.`,
      );
      return;
    }

    // Stability counter
    if (translatedCount > 0 && translatedCount === lastCountRef.current) {
      stableCountRef.current += 1;
    } else {
      stableCountRef.current = 0;
    }
    lastCountRef.current = translatedCount;

    if (translatedCount > 0 && stableCountRef.current >= STABLE_POLLS_THRESHOLD) {
      isCompletingRef.current = true;

      const complete = async () => {
        try {
          await Promise.all([
            refetchLanguages(),
            // Per-question transcripts cache — so the panel / inline
            // zone immediately see translated text
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.QUESTION_TRANSCRIPTS(
                filterId,
                categoryType,
                projectId,
                questionId,
                pendingLanguage,
              ),
            }),
            // Main transcripts cache — so the Transcripts tab picks it
            // up if the researcher switches there next
            queryClient.invalidateQueries({ queryKey: [QueryTag.CONVERSATIONS] }),
          ]);
          toast.success(`Translation to ${pendingLanguage} completed!`);
        } finally {
          setTranslatingProjects((prev) =>
            prev?.filter(
              (p) => !(p.projectId === projectId && p.source === source),
            ) || [],
          );
          isCompletingRef.current = false;
        }
      };
      complete();
    }
    // progressUpdatedAt changes on every successful poll even when
    // `progressData` is structurally identical — this makes the effect
    // re-run so stability counting works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressData, progressUpdatedAt, pendingLanguage, isTranslating]);

  // ── Handlers ────────────────────────────────────────────────────

  const handleLanguageSelection = useCallback(
    async (language: string) => {
      // Block concurrent translations. Switching between already-
      // translated languages is always fine.
      if (isTranslating && currentProjectTranslating) {
        toast.warning(
          `Translation to ${currentProjectTranslating.language} is already in progress. Please wait.`,
        );
        return;
      }

      if (translatedLanguages.includes(language)) {
        // Simple language switch — URL-only.
        const next = new URLSearchParams(searchParams);
        if (language === 'Original') {
          next.delete(SearchParam.TRANSLATION_LANGUAGE);
        } else {
          next.set(SearchParam.TRANSLATION_LANGUAGE, language);
        }
        setSearchParams(next);
        return;
      }

      // New language — kick off a translation and optimistically set
      // the URL so the view reflects what will come.
      try {
        const next = new URLSearchParams(searchParams);
        next.set(SearchParam.TRANSLATION_LANGUAGE, language);
        setSearchParams(next);

        setTranslatingProjects((prev) => [
          ...(prev || []),
          {
            projectId,
            language,
            length: conversationCount ?? 0,
            source,
          },
        ]);

        toast.info(
          `Translation to ${language} initiated. This may take a few minutes...`,
        );
        await triggerTranslation({ languages: [language] });
      } catch (err) {
        const message = getErrorMessage(err);
        toast.error(`Translation failed: ${message}`);
        // E8 / audit F-5: clean up both URL and pending-translation state
        // so the view isn't stuck in a loading state. Use the FUNCTIONAL
        // form of setSearchParams so the rollback is computed against
        // the latest params — the snapshot we captured at hook-mount
        // time can be stale by the time this catch runs (the user may
        // have changed other params in the meantime; a snapshot-based
        // rollback would clobber those changes).
        setSearchParams((prev) => {
          const rolled = new URLSearchParams(prev);
          rolled.delete(SearchParam.TRANSLATION_LANGUAGE);
          return rolled;
        });
        setTranslatingProjects((prev) =>
          prev?.filter(
            (p) => !(p.projectId === projectId && p.source === source),
          ) || [],
        );
      }
    },
    [
      isTranslating,
      currentProjectTranslating,
      translatedLanguages,
      searchParams,
      setSearchParams,
      setTranslatingProjects,
      projectId,
      conversationCount,
      source,
      triggerTranslation,
    ],
  );

  const handleDeleteLanguage = useCallback(
    async (language: string) => {
      try {
        toast.info(`Deleting ${language} translations...`);
        await deleteTranslation({ language });
        toast.success(`${language} translations deleted successfully`);
        await refetchLanguages();

        // If the user was actively viewing the deleted language,
        // fall back to Original so the panel doesn't render empty.
        if (activeLanguage === language) {
          const next = new URLSearchParams(searchParams);
          next.delete(SearchParam.TRANSLATION_LANGUAGE);
          setSearchParams(next);
        }
      } catch (err) {
        const message = getErrorMessage(err);
        toast.error(`Failed to delete ${language} translations: ${message}`);
      }
    },
    [
      deleteTranslation,
      refetchLanguages,
      activeLanguage,
      searchParams,
      setSearchParams,
    ],
  );

  const languageParam = activeLanguage === 'Original' ? null : activeLanguage;

  return {
    translationLanguage,
    activeLanguage,
    pendingLanguage,
    isTranslating,
    translatedLanguages,
    unTranslatedLanguages,
    handleLanguageSelection,
    handleDeleteLanguage,
    languageParam,
  };
}

export default useQuestionTranslation;
